import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Radio, Clock, Users, Trophy, Shield, Star, 
  BarChart3, DollarSign, Tv, ChevronRight,
  Zap, TrendingUp, Lock, Layers, Calendar,
  MessageCircle, Image, Settings, CheckCircle2,
  XCircle, HelpCircle, MapPin, Send, Megaphone,
  UserPlus, Film, Sparkles, Target,
  Eye, Share2, Pencil, ArrowUpRight, Wallet
} from 'lucide-react';

// Demo team data
const DEMO_TEAM = {
  name: 'Rooam Elite',
  sport: 'Basketball',
  record: '24-3',
  ranking: '#4 National',
  logo_color: '#FF453A',
  roster: [
    { name: 'Marcus Johnson', number: '1', position: 'PG', grade: '2027', height: "6'1\"", stats: { ppg: 18.5, apg: 7.2, rpg: 3.1 } },
    { name: 'DeAndre Williams', number: '3', position: 'SG', grade: '2026', height: "6'4\"", stats: { ppg: 22.1, apg: 3.4, rpg: 5.8 } },
    { name: 'Isaiah Thompson', number: '11', position: 'SF', grade: '2027', height: "6'6\"", stats: { ppg: 15.3, apg: 2.1, rpg: 6.9 } },
    { name: 'Jaylen Carter', number: '23', position: 'PF', grade: '2026', height: "6'8\"", stats: { ppg: 14.8, apg: 1.5, rpg: 9.2 } },
    { name: 'Terrence Davis', number: '34', position: 'C', grade: '2027', height: "6'10\"", stats: { ppg: 12.4, apg: 1.2, rpg: 11.3 } },
    { name: 'Chris Mitchell', number: '5', position: 'PG', grade: '2028', height: "5'11\"", stats: { ppg: 8.7, apg: 5.1, rpg: 2.4 } },
  ]
};

// Demo game data — real VOD from Cloudflare Stream
const DEMO_GAME = {
  home: { name: 'SoCal United Blue', score: 0, color: '#0066CC' },
  away: { name: 'High Desert Elite', score: 0, color: '#CC0000' },
  period: '4th',
  clock: '3:42',
  status: 'LIVE',
  videoId: '7a365c8c5e2127853c6e3de2996295ef',
  customerSubdomain: '74iba7d42dk3472s',
};

// Demo schedule
const DEMO_SCHEDULE = [
  { time: '9:00 AM', home: 'Rooam Elite', away: 'Desert Storm', court: 'Court 1', division: '17U Pool A', status: 'final', score: '72-58' },
  { time: '11:30 AM', home: 'Cal Stars EYBL', away: 'Rooam Elite', court: 'Court 2', division: '17U Pool A', status: 'live', score: '61-67' },
  { time: '2:00 PM', home: 'Rooam Elite', away: 'MW Premier', court: 'Court 1', division: '17U Pool A', status: 'upcoming' },
  { time: '4:30 PM', home: 'Hardwood Elite', away: 'Rooam Elite', court: 'Court 3', division: '17U Bracket', status: 'upcoming' },
];

export default function DemoPage() {
  const [activeSection, setActiveSection] = useState('overview');
  const [programTab, setProgramTab] = useState('overview');
  const [teamTab, setTeamTab] = useState('dashboard');

  const sections = [
    { id: 'overview', label: 'Platform Overview' },
    { id: 'game', label: 'Live Game' },
    { id: 'analytics', label: 'AI Analytics' },
    { id: 'program', label: 'Program OS' },
    { id: 'team', label: 'Team HQ' },
    { id: 'monetization', label: 'Monetization' },
    { id: 'creator', label: 'Creator Dashboard' },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-black to-black" />
        <div className="relative max-w-7xl mx-auto px-4 pt-16 pb-12">
          <div className="flex items-center gap-3 mb-6">
            <img src="/rooam-logo.png" alt="Rooam" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
            <Badge className="bg-red-600/20 text-red-400 border border-red-600/30">DEMO</Badge>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
            THE COMMAND CENTER<br />
            <span className="text-red-500">FOR SPORTS</span>
          </h1>
          <p className="text-lg text-zinc-400 mt-4 max-w-2xl">
            Live streaming, real-time scoring, team management, AI analytics, and monetization — all in one platform built for the $30B youth sports market.
          </p>
        </div>
      </div>

      {/* Section Nav */}
      <div className="sticky top-0 z-50 bg-black/90 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-3">
            {sections.map(s => (
              <button
                key={s.id}
                onClick={() => {
                  setActiveSection(s.id);
                  document.getElementById(`demo-${s.id}`)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                  activeSection === s.id ? 'bg-red-600 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-16">

        {/* ============ PLATFORM OVERVIEW ============ */}
        <section id="demo-overview">
          <SectionHeader icon={<Zap />} title="Platform Overview" subtitle="Everything a sports organization needs" />
          
          {/* Video Embed — native 9:16 aspect to fit the vertical source without black bars */}
          <div className="mb-10 flex justify-center">
            <div className="relative w-full max-w-[420px] rounded-2xl overflow-hidden border border-white/10 bg-zinc-900" style={{ aspectRatio: '9 / 16' }}>
              <iframe
                src="https://iframe.cloudflarestream.com/299d201a74ea1f8c59f11a356fc80750?poster=https%3A%2F%2Fcustomer-74iba7d42dk3472s.cloudflarestream.com%2F299d201a74ea1f8c59f11a356fc80750%2Fthumbnails%2Fthumbnail.jpg"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                title="Rooam Sports Platform Overview"
                data-testid="demo-platform-overview-video"
              />
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: <Tv />, title: 'Multi-Court Streaming', desc: 'Cloudflare-powered live streams with multi-court support, custom overlays, and automatic VOD recording.' },
              { icon: <Trophy />, title: 'Real-Time Scoring', desc: 'Operator console for live score updates, period tracking, and stat logging — all synced to viewers instantly.' },
              { icon: <Shield />, title: 'Team HQ', desc: 'Full team profiles with rosters, schedules, game history, and performance analytics for every program.' },
              { icon: <BarChart3 />, title: 'AI Analytics', desc: 'SportsVisio integration — auto-generated box scores, AI game breakdowns, coach reports, highlight reels, and athlete stat claiming.' },
              { icon: <Users />, title: 'Athlete Profiles', desc: 'Verified athlete profiles with highlight reels, stats, and recruitment-ready information.' },
              { icon: <DollarSign />, title: 'Monetization', desc: 'Membership tiers, event passes, discount codes, and Stripe-powered payments built in.' },
            ].map((f, i) => (
              <Card key={i} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-all">
                <CardContent className="p-5">
                  <div className="text-red-500 mb-3">{f.icon}</div>
                  <h3 className="text-white font-bold text-sm mb-1">{f.title}</h3>
                  <p className="text-zinc-500 text-xs leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ============ LIVE GAME DEMO ============ */}
        <section id="demo-game">
          <SectionHeader icon={<Radio />} title="Live Game Experience" subtitle="What viewers see during a live broadcast" />

          {/* Scoreboard */}
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-zinc-900">
            {/* Video Area — Real VOD */}
            <div className="relative bg-black aspect-video">
              <iframe
                src={`https://iframe.cloudflarestream.com/${DEMO_GAME.videoId}?autoplay=true&muted=true&loop=true&controls=true`}
                className="w-full h-full"
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
                title="Rooam Sports Live Game"
                style={{ border: 'none' }}
              />
              
              {/* LIVE badge */}
              <div className="absolute top-4 left-4 flex items-center gap-2 pointer-events-none">
                <Badge className="bg-red-600 text-white font-bold text-xs px-3 py-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse mr-1.5" />
                  LIVE
                </Badge>
              </div>
            </div>

            {/* Score Bar */}
            <div className="bg-black px-4 py-3">
              <div className="flex items-center justify-between max-w-lg mx-auto">
                {/* Home */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-black text-sm" style={{ backgroundColor: DEMO_GAME.home.color }}>
                    SCU
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">{DEMO_GAME.home.name}</p>
                    <p className="text-zinc-500 text-[10px] uppercase">Home</p>
                  </div>
                </div>
                
                {/* VS / Replay label */}
                <div className="text-center">
                  <Badge className="bg-red-600/20 text-red-400 text-xs px-3 py-1">
                    VOD REPLAY
                  </Badge>
                </div>
                
                {/* Away */}
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-white font-bold text-sm">{DEMO_GAME.away.name}</p>
                    <p className="text-zinc-500 text-[10px] uppercase">Away</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-black text-sm" style={{ backgroundColor: DEMO_GAME.away.color }}>
                    HDE
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Event Schedule */}
          <div className="mt-6">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" />
              Event Schedule
            </h3>
            <div className="space-y-1">
              {DEMO_SCHEDULE.map((game, i) => (
                <div key={i} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                  game.status === 'live' ? 'bg-red-500/10 border-red-500/30' : 
                  game.status === 'final' ? 'bg-zinc-900/30 border-zinc-800' : 
                  'bg-zinc-900/50 border-zinc-800'
                }`}>
                  <div className="w-16 shrink-0 text-right">
                    <span className={`text-sm font-semibold ${
                      game.status === 'live' ? 'text-red-400' : game.status === 'final' ? 'text-zinc-500' : 'text-amber-400'
                    }`}>{game.time}</span>
                  </div>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${
                    game.status === 'live' ? 'bg-red-500 animate-pulse' : game.status === 'final' ? 'bg-emerald-500' : 'bg-zinc-600'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${game.status === 'final' ? 'text-zinc-400' : 'text-white'}`}>
                      {game.home} <span className="text-zinc-600 font-normal">vs</span> {game.away}
                    </p>
                    <p className="text-[10px] text-zinc-600">{game.division} &middot; {game.court}</p>
                  </div>
                  {game.score && (
                    <span className={`text-sm font-bold shrink-0 ${game.status === 'live' ? 'text-white' : 'text-zinc-500'}`}>{game.score}</span>
                  )}
                  <Badge className={`text-[10px] px-2 py-0.5 shrink-0 ${
                    game.status === 'live' ? 'bg-red-600 text-white' : game.status === 'final' ? 'bg-emerald-600 text-white' : 'bg-zinc-700 text-zinc-300'
                  }`}>
                    {game.status === 'live' ? 'LIVE' : game.status === 'final' ? 'FINAL' : 'UPCOMING'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ AI ANALYTICS SHOWCASE ============ */}
        <section id="demo-analytics">
          <SectionHeader icon={<BarChart3 />} title="AI-Powered Analytics" subtitle="SportsVisio Integration" />

          {/* Partnership Banner */}
          <div className="flex items-center justify-center gap-4 py-4 px-6 mb-6 bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 border border-white/5 rounded-xl">
            <span className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-medium">Advanced Analytics by</span>
            <img src="/sportsvisio-logo.png" alt="SportsVisio" className="h-6 opacity-90" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Left — Feature Highlights */}
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm leading-relaxed">
                Every game streamed on Rooam is automatically analyzed by SportsVisio's AI — generating comprehensive box scores, game narratives, coaching insights, and highlight reels. Athletes can claim their stats to build verified performance profiles.
              </p>

              <div className="space-y-3">
                {[
                  { icon: <BarChart3 className="w-5 h-5" />, title: 'Full Box Score', desc: 'Complete stat lines for every player — PTS, REB, AST, STL, BLK, FG%, 3PT%, FT% — generated automatically from game footage.', color: 'text-cyan-400' },
                  { icon: <Sparkles className="w-5 h-5" />, title: 'AI Game Breakdown', desc: 'Natural language game summary — key plays, momentum shifts, standout performers, and tactical analysis written by AI.', color: 'text-amber-400' },
                  { icon: <Target className="w-5 h-5" />, title: 'Coach Report', desc: 'Per-team strengths and areas for improvement — actionable insights coaches can use for game prep and player development.', color: 'text-emerald-400' },
                  { icon: <Film className="w-5 h-5" />, title: 'Auto Highlights', desc: '90-second highlight reels generated automatically from the full game — key plays, dunks, blocks, and clutch moments.', color: 'text-red-400' },
                  { icon: <UserPlus className="w-5 h-5" />, title: 'Claim Your Stats', desc: 'Athletes claim their jersey number to build a verified profile with career stats, game logs, and personal highlight reels across events.', color: 'text-purple-400' },
                ].map((f, i) => (
                  <div key={i} className="flex gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-all">
                    <div className={`${f.color} shrink-0 mt-0.5`}>{f.icon}</div>
                    <div>
                      <h4 className="text-white font-semibold text-sm mb-0.5">{f.title}</h4>
                      <p className="text-zinc-500 text-xs leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Live Box Score Demo */}
            <div>
              <Card className="bg-[#0a0a0a] border-white/5 overflow-hidden">
                {/* SportsVisio Banner */}
                <div className="flex items-center justify-center gap-3 py-2.5 px-4 bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 border-b border-white/5">
                  <span className="text-[9px] text-zinc-500 uppercase tracking-[0.2em]">Advanced Analytics by</span>
                  <img src="/sportsvisio-logo.png" alt="SportsVisio" className="h-4 opacity-80" />
                </div>

                {/* Demo Tabs */}
                <div className="flex border-b border-white/5">
                  {['Box Score', 'AI Breakdown', 'Coach Report'].map((tab, i) => (
                    <button key={tab} className={`flex-1 py-2.5 text-[10px] font-medium tracking-wide uppercase ${i === 0 ? 'text-white border-b-2 border-white' : 'text-zinc-600'}`}>
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Demo Box Score */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/5">
                    <span className="text-[10px] font-bold text-white uppercase tracking-wider">Vice City</span>
                    <span className="text-[10px] text-zinc-600">57 PTS</span>
                  </div>
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="text-zinc-600 uppercase tracking-wider">
                        <th className="text-left py-1.5 pr-1 font-medium">#</th>
                        <th className="text-left py-1.5 pr-2 font-medium">Player</th>
                        <th className="text-center py-1.5 px-1 font-medium">PTS</th>
                        <th className="text-center py-1.5 px-1 font-medium">REB</th>
                        <th className="text-center py-1.5 px-1 font-medium">AST</th>
                        <th className="text-center py-1.5 px-1 font-medium">FG</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { num: 0, name: 'Marcus J.', pts: 17, reb: 5, ast: 0, fg: '7-14', claimed: true, claimName: 'Marcus Johnson' },
                        { num: 13, name: '#13', pts: 14, reb: 8, ast: 0, fg: '6-12' },
                        { num: 10, name: '#10', pts: 9, reb: 2, ast: 0, fg: '2-5' },
                        { num: 9, name: '#9', pts: 5, reb: 2, ast: 2, fg: '1-4' },
                        { num: 30, name: '#30', pts: 4, reb: 3, ast: 2, fg: '2-8' },
                      ].map((p, i) => (
                        <tr key={i} className={`border-t border-white/3 ${p.claimed ? 'bg-cyan-500/5' : i === 0 ? 'bg-white/2' : ''}`}>
                          <td className="py-2 pr-1 text-zinc-500 font-mono">{p.num}</td>
                          <td className="py-2 pr-2">
                            <span className={`font-medium ${p.claimed ? 'text-cyan-400' : 'text-zinc-300'}`}>
                              {p.claimed ? p.claimName : p.name}
                            </span>
                            {p.claimed && <span className="ml-1 text-[8px] text-cyan-500 bg-cyan-500/10 px-1 rounded">Verified</span>}
                          </td>
                          <td className={`text-center py-2 px-1 font-bold ${p.pts >= 15 ? 'text-white' : p.pts >= 10 ? 'text-zinc-200' : 'text-zinc-400'}`}>{p.pts}</td>
                          <td className={`text-center py-2 px-1 ${p.reb >= 5 ? 'text-white' : 'text-zinc-500'}`}>{p.reb}</td>
                          <td className="text-center py-2 px-1 text-zinc-500">{p.ast}</td>
                          <td className="text-center py-2 px-1 text-zinc-500">{p.fg}</td>
                          <td className="text-right py-2 pl-1">
                            {p.claimed ? (
                              <ChevronRight className="h-3 w-3 text-cyan-500 inline" />
                            ) : (
                              <span className="text-[8px] text-zinc-700 flex items-center gap-0.5"><UserPlus className="h-2.5 w-2.5" /></span>
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-white/10 font-semibold">
                        <td className="py-2"></td>
                        <td className="py-2 text-zinc-400 uppercase text-[9px] tracking-wider">Totals</td>
                        <td className="text-center py-2 px-1 text-white">57</td>
                        <td className="text-center py-2 px-1 text-zinc-400">33</td>
                        <td className="text-center py-2 px-1 text-zinc-400">8</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Claim CTA */}
                  <div className="mt-4 p-3 rounded-lg bg-gradient-to-r from-cyan-950/20 to-zinc-900 border border-cyan-900/30">
                    <p className="text-cyan-400 text-[10px] font-bold uppercase tracking-wider mb-1">Athlete Stat Claiming</p>
                    <p className="text-zinc-500 text-[10px] leading-relaxed">Athletes with a Rooam Elite membership can claim their jersey number to link comprehensive SportsVisio stats to their profile — across teams, events, and seasons.</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* AI Summary Showcase */}
          <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
            <div className="p-5 border-b border-zinc-800">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-xs font-bold uppercase tracking-wider">AI Game Breakdown — Sample</span>
              </div>
            </div>
            <CardContent className="p-5">
              <p className="text-zinc-300 text-sm leading-[1.8]">
                Vice City opened the season with a hard-fought 57-51 victory over United 5. Shooting a modest 21/55 (38.2% FG, 5/24 3PT) but dominating the glass (12 ORB, 33 TRB total), Vice City used timely plays and strong defense to overcome United 5's 21/51 (41.2% FG, 3/18 3PT) shooting. United 5 managed only 2 AST and turned it over 9 times vs. VC's 7 turnovers and 8 AST.
              </p>
              <p className="text-zinc-400 text-sm leading-[1.8] mt-3">
                United 5's No. 13 led all scorers with 21 PTS on 9-15 FG, pulled down 9 TRB (3 ORB), added 2 STL. No. 2 chipped in 15 PTS (6-17, 1 3PM), 5 TRB, 1 AST but struggled with 4 TO. Vice City balanced offense: No. 0 topped VC with 17 PTS on 7-14 FG, 5 TRB; No. 13 added 14 PTS, 8 TRB, 3 STL.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* ============ PROGRAM OS ============ */}
        <section id="demo-program">
          <SectionHeader icon={<Layers />} title="Program OS" subtitle="Your organization's command center" />

          <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
            {/* Program Header */}
            <div className="border-b border-zinc-800 p-4 sm:p-6" style={{ borderBottomColor: '#06b6d433' }}>
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center text-base sm:text-lg font-black text-white shrink-0" style={{ backgroundColor: '#06b6d4' }}>
                  CE
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <h2 className="text-base sm:text-2xl font-bold text-white leading-tight">Charlotte Elite Basketball</h2>
                    <div className="shrink-0 mt-0.5 w-7 h-7 rounded border border-zinc-700 flex items-center justify-center">
                      <Settings className="w-3.5 h-3.5 text-zinc-400" />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500 mt-1">
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0" style={{ backgroundColor: '#06b6d420', color: '#06b6d4', borderColor: '#06b6d444' }}>Basketball</Badge>
                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> Charlotte, NC</span>
                    <span className="flex items-center gap-0.5"><Layers className="w-3 h-3" /> 4 teams</span>
                    <span className="flex items-center gap-0.5"><Users className="w-3 h-3" /> 48 members</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Bar */}
            <div className="border-b border-zinc-800 bg-zinc-950/50">
              <div className="flex gap-0.5 overflow-x-auto px-4">
                {[
                  { id: 'overview', label: 'Overview', icon: <Layers className="w-3.5 h-3.5" /> },
                  { id: 'game-hub', label: 'Game Hub', icon: <Tv className="w-3.5 h-3.5" /> },
                  { id: 'schedule', label: 'Schedule', icon: <Calendar className="w-3.5 h-3.5" /> },
                  { id: 'roster', label: 'Roster', icon: <Users className="w-3.5 h-3.5" /> },
                  { id: 'media', label: 'Media', icon: <Image className="w-3.5 h-3.5" /> },
                  { id: 'chat', label: 'Chat', icon: <MessageCircle className="w-3.5 h-3.5" /> },
                ].map((tab) => (
                  <button key={tab.id} onClick={() => setProgramTab(tab.id)} className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    programTab === tab.id ? 'text-cyan-400 border-cyan-400' : 'text-zinc-500 border-transparent hover:text-zinc-300'
                  }`}>
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Program Tab Content */}
            <CardContent className="p-4 sm:p-6">
              {programTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Teams Column */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-cyan-400" /> Teams</h3>
                    <div className="w-6 h-6 rounded bg-cyan-500/20 flex items-center justify-center"><UserPlus className="w-3 h-3 text-cyan-400" /></div>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: '17U Elite', level: 'Gold', record: '24-3', color: '#FF453A' },
                      { name: '16U Select', level: 'Silver', record: '18-7', color: '#06b6d4' },
                      { name: '15U Development', level: 'Bronze', record: '12-10', color: '#f97316' },
                      { name: '14U Future Stars', level: 'Bronze', record: '8-5', color: '#a855f7' },
                    ].map((t, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-xs shrink-0" style={{ backgroundColor: t.color }}>
                          {t.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm truncate">{t.name}</p>
                          <p className="text-zinc-500 text-[10px]">{t.record} record</p>
                        </div>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 ${
                          t.level === 'Gold' ? 'text-amber-400 border-amber-700' : t.level === 'Silver' ? 'text-zinc-300 border-zinc-600' : 'text-orange-400 border-orange-700'
                        }`}>{t.level}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upcoming Schedule */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-400" /> Upcoming</h3>
                  </div>
                  <div className="space-y-2">
                    {[
                      { type: 'game', title: '17U Elite vs Rock Hill Rising', time: 'Sat, Apr 12 · 9:00 AM', loc: 'Sportsplex Court 1' },
                      { type: 'practice', title: '16U Select Practice', time: 'Mon, Apr 14 · 4:30 PM', loc: 'Freedom Park Gym' },
                      { type: 'tournament', title: 'Spring Classic Showcase', time: 'Apr 18-20', loc: 'Charlotte Convention Center' },
                      { type: 'film_session', title: '17U Film Review', time: 'Tue, Apr 15 · 6:00 PM', loc: 'Virtual' },
                    ].map((e, i) => {
                      const styles = { game: 'bg-red-500/10 border-red-800 text-red-400', practice: 'bg-emerald-500/10 border-emerald-800 text-emerald-400', tournament: 'bg-amber-500/10 border-amber-800 text-amber-400', film_session: 'bg-blue-500/10 border-blue-800 text-blue-400' };
                      return (
                        <div key={i} className={`p-2.5 rounded-lg border ${styles[e.type]?.split(' ').slice(0, 2).join(' ') || 'bg-zinc-800/50 border-zinc-700'}`}>
                          <p className="text-white text-sm font-semibold">{e.title}</p>
                          <p className="text-zinc-500 text-[10px] mt-0.5 flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {e.time}</p>
                          <p className="text-zinc-600 text-[10px] flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {e.loc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Members & Roles */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-bold text-sm flex items-center gap-2"><Users className="w-4 h-4 text-emerald-400" /> Members</h3>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { name: 'Coach Davis', role: 'program_admin', team: '17U Elite' },
                      { name: 'Coach Simmons', role: 'coach', team: '16U Select' },
                      { name: 'Sarah Johnson', role: 'parent', team: '17U Elite' },
                      { name: 'Mike Williams', role: 'parent', team: '17U Elite' },
                      { name: 'Lisa Thompson', role: 'parent', team: '15U Development' },
                    ].map((m, i) => {
                      const roleColors = { program_admin: 'text-amber-400 bg-amber-500/10 border-amber-800', coach: 'text-cyan-400 bg-cyan-500/10 border-cyan-800', parent: 'text-zinc-400 bg-zinc-500/10 border-zinc-700' };
                      return (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800/50 transition-colors">
                          <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-white font-bold text-[10px]">
                            {m.name.split(' ').map(w => w[0]).join('')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{m.name}</p>
                            <p className="text-zinc-600 text-[10px]">{m.team}</p>
                          </div>
                          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${roleColors[m.role] || ''}`}>
                            {m.role === 'program_admin' ? 'Admin' : m.role === 'coach' ? 'Coach' : 'Parent'}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>

                  {/* Announcements */}
                  <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-800">
                    <div className="flex items-center gap-2 mb-1">
                      <Megaphone className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-amber-400 text-xs font-bold">Announcement</span>
                    </div>
                    <p className="text-zinc-300 text-xs">Spring Classic schedule posted! Check the Schedule tab for court assignments and game times.</p>
                    <p className="text-zinc-600 text-[10px] mt-1">Coach Davis · 2 hours ago</p>
                  </div>
                </div>
              </div>
              )}

              {programTab === 'game-hub' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-sm">Watch Sessions</h3>
                  <button className="text-[10px] px-3 py-1 rounded-lg bg-cyan-600 text-white font-semibold">+ New Session</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { title: '17U Elite vs Desert Storm — Full Game', type: 'Cloudflare VOD', date: 'Apr 5, 2026', duration: '1h 12m', views: 24 },
                    { title: '16U Select Highlights — Spring Classic', type: 'Custom Upload', date: 'Apr 3, 2026', duration: '8m 30s', views: 41 },
                    { title: '17U Film Review — Rock Hill Breakdown', type: 'Film Session', date: 'Apr 1, 2026', duration: '22m', views: 12 },
                    { title: '15U Dev vs Metro Youth — Pool Play', type: 'Cloudflare VOD', date: 'Mar 29, 2026', duration: '58m', views: 18 },
                  ].map((s, i) => (
                    <div key={i} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50 hover:border-zinc-600 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="w-16 h-10 rounded bg-zinc-700 flex items-center justify-center shrink-0">
                          <Film className="w-4 h-4 text-zinc-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold truncate">{s.title}</p>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500">
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-zinc-700 text-zinc-400">{s.type}</Badge>
                            <span>{s.date}</span>
                            <span>{s.duration}</span>
                            <span>{s.views} views</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {programTab === 'schedule' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-sm">Full Schedule</h3>
                  <button className="text-[10px] px-3 py-1 rounded-lg bg-cyan-600 text-white font-semibold">+ Add Event</button>
                </div>
                <div className="space-y-2">
                  {[
                    { type: 'game', title: '17U Elite vs Rock Hill Rising', time: 'Sat, Apr 12 · 9:00 AM', loc: 'Sportsplex Court 1', team: '17U Elite' },
                    { type: 'game', title: '16U Select vs Lake Norman', time: 'Sat, Apr 12 · 10:30 AM', loc: 'Sportsplex Court 2', team: '16U Select' },
                    { type: 'practice', title: '15U Development Practice', time: 'Mon, Apr 14 · 4:00 PM', loc: 'Freedom Park Gym', team: '15U Dev' },
                    { type: 'practice', title: '16U Select Practice', time: 'Mon, Apr 14 · 4:30 PM', loc: 'Freedom Park Gym', team: '16U Select' },
                    { type: 'film_session', title: '17U Film Review', time: 'Tue, Apr 15 · 6:00 PM', loc: 'Virtual / Zoom', team: '17U Elite' },
                    { type: 'tournament', title: 'Spring Classic Showcase', time: 'Apr 18-20 · All Day', loc: 'Charlotte Convention Center', team: 'All Teams' },
                    { type: 'game', title: '14U Future Stars vs Gastonia', time: 'Sat, Apr 19 · 8:00 AM', loc: 'Convention Center Court 4', team: '14U Future' },
                  ].map((e, i) => {
                    const colors = { game: 'border-l-red-500', practice: 'border-l-emerald-500', tournament: 'border-l-amber-500', film_session: 'border-l-blue-500' };
                    return (
                      <div key={i} className={`flex items-center gap-3 p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50 border-l-4 ${colors[e.type] || 'border-l-zinc-500'}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold">{e.title}</p>
                          <p className="text-zinc-500 text-[10px] mt-0.5">{e.time} · {e.loc}</p>
                        </div>
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-zinc-700 text-zinc-400 shrink-0">{e.team}</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {programTab === 'roster' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-sm">All Members (48)</h3>
                  <button className="text-[10px] px-3 py-1 rounded-lg bg-cyan-600 text-white font-semibold">+ Add Member</button>
                </div>
                {[
                  { team: '17U Elite', color: '#FF453A', members: [
                    { name: 'Coach Davis', role: 'program_admin' }, { name: 'Marcus Johnson', role: 'athlete', num: '#1' },
                    { name: 'DeAndre Williams', role: 'athlete', num: '#3' }, { name: 'Sarah Johnson', role: 'parent' }, { name: 'Mike Williams', role: 'parent' },
                  ]},
                  { team: '16U Select', color: '#06b6d4', members: [
                    { name: 'Coach Simmons', role: 'coach' }, { name: 'Tyler Brooks', role: 'athlete', num: '#12' },
                    { name: 'Andre Foster', role: 'athlete', num: '#24' }, { name: 'Karen Brooks', role: 'parent' },
                  ]},
                  { team: '15U Development', color: '#f97316', members: [
                    { name: 'Coach Martinez', role: 'coach' }, { name: 'Ryan Lee', role: 'athlete', num: '#7' }, { name: 'Lisa Thompson', role: 'parent' },
                  ]},
                ].map((group, i) => (
                  <div key={i} className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 rounded flex items-center justify-center text-white font-bold text-[8px]" style={{ backgroundColor: group.color }}>
                        {group.team.split(' ').map(w => w[0]).join('').slice(0, 2)}
                      </div>
                      <span className="text-zinc-400 text-xs font-semibold">{group.team}</span>
                    </div>
                    <div className="space-y-1 ml-7">
                      {group.members.map((m, j) => {
                        const roleColors = { program_admin: 'text-amber-400 border-amber-800', coach: 'text-cyan-400 border-cyan-800', athlete: 'text-emerald-400 border-emerald-800', parent: 'text-zinc-400 border-zinc-700' };
                        return (
                          <div key={j} className="flex items-center gap-3 p-1.5 rounded hover:bg-zinc-800/50 transition-colors">
                            <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-white font-bold text-[9px]">
                              {m.num || m.name.split(' ').map(w => w[0]).join('')}
                            </div>
                            <span className="text-white text-sm flex-1">{m.name}</span>
                            <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${roleColors[m.role] || ''}`}>
                              {m.role === 'program_admin' ? 'Admin' : m.role.charAt(0).toUpperCase() + m.role.slice(1)}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              )}

              {programTab === 'media' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-sm">Media Gallery</h3>
                  <button className="text-[10px] px-3 py-1 rounded-lg bg-cyan-600 text-white font-semibold">+ Upload</button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[
                    { title: 'Team Photo — Spring Classic', type: 'photo', color: '#374151' },
                    { title: '17U Warmup Highlight', type: 'video', color: '#1f2937' },
                    { title: 'Court 1 Overhead', type: 'photo', color: '#292524' },
                    { title: 'DeAndre Dunk Clip', type: 'video', color: '#1c1917' },
                    { title: 'Championship Banner', type: 'photo', color: '#27272a' },
                    { title: 'Practice Drills B-Roll', type: 'video', color: '#18181b' },
                  ].map((m, i) => (
                    <div key={i} className="rounded-lg overflow-hidden border border-zinc-700/50 group hover:border-zinc-600 transition-colors">
                      <div className="aspect-video flex items-center justify-center" style={{ backgroundColor: m.color }}>
                        {m.type === 'video' ? <Film className="w-6 h-6 text-zinc-600" /> : <Image className="w-6 h-6 text-zinc-600" />}
                      </div>
                      <div className="p-2 bg-zinc-900">
                        <p className="text-white text-[11px] font-medium truncate">{m.title}</p>
                        <Badge variant="outline" className="text-[8px] px-1 py-0 border-zinc-700 text-zinc-500 mt-1">{m.type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}

              {programTab === 'chat' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-sm">Program Chat</h3>
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-amber-400 border-amber-800">All Members</Badge>
                </div>
                <div className="space-y-3 mb-4">
                  {[
                    { from: 'Coach Davis', msg: 'Reminder: Spring Classic brackets drop tomorrow at noon. Make sure all players have their eligibility paperwork submitted.', time: '10:15 AM', admin: true },
                    { from: 'Lisa Thompson', msg: 'Ryan is good to go. Forms submitted yesterday.', time: '10:22 AM' },
                    { from: 'Coach Simmons', msg: '16U crew — we have a film session Tuesday at 6. Zoom link will be in the Schedule tab. Mandatory for starters.', time: '11:04 AM', admin: true },
                    { from: 'Sarah Johnson', msg: 'Is there a team dinner Friday before the showcase?', time: '11:30 AM' },
                    { from: 'Coach Davis', msg: 'Yes! Golden Corral at 6pm. Details in Announcements.', time: '11:32 AM', admin: true },
                  ].map((m, i) => (
                    <div key={i} className="flex gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-white font-bold text-[9px] shrink-0">
                        {m.from.split(' ').map(w => w[0]).join('')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-xs font-semibold">{m.from}</span>
                          {m.admin && <Badge variant="outline" className="text-[8px] px-1 py-0 text-amber-400 border-amber-800">Admin</Badge>}
                          <span className="text-zinc-600 text-[10px]">{m.time}</span>
                        </div>
                        <p className="text-zinc-400 text-xs mt-0.5 leading-relaxed">{m.msg}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 bg-zinc-800 rounded-lg px-3 py-2 text-zinc-500 text-xs border border-zinc-700">Type a message...</div>
                  <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center cursor-pointer"><Send className="w-3.5 h-3.5 text-white" /></div>
                </div>
              </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* ============ TEAM HQ DEMO ============ */}
        <section id="demo-team">
          <SectionHeader icon={<Shield />} title="Team HQ" subtitle="Every team gets a dedicated command center" />

          <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
            {/* Team Header */}
            <div className="relative overflow-hidden p-4 sm:p-6 border-b border-white/10" style={{ background: 'linear-gradient(135deg, #1a0a0a 0%, #0a0a0a 100%)' }}>
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-[80px] opacity-15" style={{ backgroundColor: '#FF453A' }} />
              <div className="relative flex items-start gap-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-red-600 flex items-center justify-center text-lg font-black text-white shrink-0 shadow-xl" style={{ fontFamily: 'Oswald, sans-serif', boxShadow: '0 8px 24px #FF453A30' }}>
                  RE
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {DEMO_TEAM.name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-zinc-700 text-zinc-400">17U</Badge>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-400 border-amber-700">Gold</Badge>
                    <Badge className="bg-zinc-800 text-zinc-300 text-xs">{DEMO_TEAM.sport}</Badge>
                    <span className="text-emerald-400 text-sm font-bold">{DEMO_TEAM.record}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Tabs */}
            <div className="border-b border-zinc-800 bg-zinc-950/50">
              <div className="flex gap-0.5 overflow-x-auto px-4">
                {[
                  { id: 'dashboard', label: 'Dashboard', icon: <Layers className="w-3.5 h-3.5" /> },
                  { id: 'roster', label: 'Roster', icon: <Users className="w-3.5 h-3.5" /> },
                  { id: 'schedule', label: 'Schedule', icon: <Calendar className="w-3.5 h-3.5" /> },
                  { id: 'messages', label: 'Messages', icon: <MessageCircle className="w-3.5 h-3.5" /> },
                  { id: 'media', label: 'Media', icon: <Image className="w-3.5 h-3.5" /> },
                ].map((tab) => (
                  <button key={tab.id} onClick={() => setTeamTab(tab.id)} className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                    teamTab === tab.id ? 'text-cyan-400 border-cyan-400' : 'text-zinc-500 border-transparent hover:text-zinc-300'
                  }`}>
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Team Tab Content */}
            {teamTab === 'dashboard' && (
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Next Game */}
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-800">
                  <p className="text-red-400 text-[10px] uppercase font-bold tracking-wide mb-2">Next Game</p>
                  <p className="text-white text-lg font-bold">vs Rock Hill Rising</p>
                  <p className="text-zinc-400 text-sm mt-1">Sat, Apr 12 · 9:00 AM · Sportsplex Court 1</p>
                  <div className="flex items-center gap-3 mt-3">
                    <div className="flex items-center gap-1 text-[10px]"><CheckCircle2 className="w-3 h-3 text-emerald-400" /><span className="text-emerald-400 font-semibold">8 Going</span></div>
                    <div className="flex items-center gap-1 text-[10px]"><HelpCircle className="w-3 h-3 text-amber-400" /><span className="text-amber-400 font-semibold">2 Maybe</span></div>
                  </div>
                </div>
                {/* Recent Results */}
                <div>
                  <p className="text-zinc-400 text-[10px] uppercase font-bold tracking-wide mb-2">Recent Results</p>
                  <div className="space-y-2">
                    {[
                      { vs: 'Desert Storm', result: 'W', score: '72-58', date: 'Apr 5' },
                      { vs: 'Cal Stars EYBL', result: 'W', score: '67-61', date: 'Apr 3' },
                      { vs: 'MW Premier', result: 'L', score: '55-60', date: 'Mar 29' },
                    ].map((g, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-800/50">
                        <Badge className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded ${g.result === 'W' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>{g.result}</Badge>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold">vs {g.vs}</p>
                          <p className="text-zinc-600 text-[10px]">{g.date}</p>
                        </div>
                        <span className="text-zinc-300 text-sm font-bold">{g.score}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Season Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {[
                  { label: 'Record', value: '24-3' },
                  { label: 'PPG', value: '68.4' },
                  { label: 'Opp PPG', value: '54.2' },
                  { label: 'Win Streak', value: '7' },
                ].map((s, i) => (
                  <div key={i} className="text-center p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50">
                    <p className="text-white text-lg font-black" style={{ fontFamily: 'Oswald, sans-serif' }}>{s.value}</p>
                    <p className="text-zinc-500 text-[10px]">{s.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
            )}

            {teamTab === 'roster' && (
            <CardContent className="p-0">
              <div className="divide-y divide-zinc-800/50">
                {DEMO_TEAM.roster.map((player, i) => (
                  <div key={i} className="flex items-center gap-4 px-4 sm:px-6 py-3 hover:bg-white/5 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-white font-bold text-sm">
                      #{player.number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">{player.name}</p>
                      <p className="text-zinc-500 text-xs">{player.position} &middot; {player.height} &middot; Class of {player.grade}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-4 text-xs">
                      <div className="text-center">
                        <p className="text-white font-bold">{player.stats.ppg}</p>
                        <p className="text-zinc-600">PPG</p>
                      </div>
                      <div className="text-center">
                        <p className="text-white font-bold">{player.stats.apg}</p>
                        <p className="text-zinc-600">APG</p>
                      </div>
                      <div className="text-center">
                        <p className="text-white font-bold">{player.stats.rpg}</p>
                        <p className="text-zinc-600">RPG</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
                  </div>
                ))}
              </div>
            </CardContent>
            )}

            {teamTab === 'schedule' && (
            <CardContent className="p-4 sm:p-6">
              <h3 className="text-white font-bold text-sm flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-amber-400" /> Upcoming &middot; RSVP
              </h3>
              <div className="space-y-2">
                {[
                  { title: 'vs Rock Hill Rising', time: 'Sat 9:00 AM', loc: 'Court 1', going: 8, maybe: 2, no: 1 },
                  { title: 'Practice', time: 'Mon 4:30 PM', loc: 'Freedom Park', going: 5, maybe: 3, no: 0 },
                  { title: 'vs Metro Youth Varsity', time: 'Sat 11:30 AM', loc: 'Court 2', going: 7, maybe: 1, no: 2 },
                  { title: 'Film Session — Rock Hill Tape', time: 'Tue 6:00 PM', loc: 'Virtual / Zoom', going: 4, maybe: 2, no: 0 },
                ].map((evt, i) => (
                  <div key={i} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-semibold">{evt.title}</p>
                        <p className="text-zinc-500 text-[10px]">{evt.time} &middot; {evt.loc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex items-center gap-1 text-[10px]"><CheckCircle2 className="w-3 h-3 text-emerald-400" /><span className="text-emerald-400 font-semibold">{evt.going}</span></div>
                      <div className="flex items-center gap-1 text-[10px]"><HelpCircle className="w-3 h-3 text-amber-400" /><span className="text-amber-400 font-semibold">{evt.maybe}</span></div>
                      <div className="flex items-center gap-1 text-[10px]"><XCircle className="w-3 h-3 text-red-400" /><span className="text-red-400 font-semibold">{evt.no}</span></div>
                      <div className="flex-1" />
                      <div className="flex gap-1">
                        {['Going', 'Maybe'].map((s, j) => (
                          <button key={j} className={`text-[10px] px-2 py-0.5 rounded-full border ${j === 0 ? 'bg-emerald-500/20 border-emerald-700 text-emerald-400' : 'bg-zinc-800 border-zinc-700 text-zinc-400'}`}>{s}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
            )}

            {teamTab === 'messages' && (
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-bold text-sm flex items-center gap-2"><MessageCircle className="w-4 h-4 text-cyan-400" /> Team Chat</h3>
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-amber-400 border-amber-800">17U Elite</Badge>
              </div>
              <div className="space-y-3 mb-4">
                {[
                  { from: 'Coach Davis', msg: 'Great practice today! Make sure everyone has their travel forms in by Friday.', time: '4:32 PM', admin: true },
                  { from: 'Sarah Johnson', msg: 'Marcus will be 10 min late to Saturday game - dentist appointment', time: '5:15 PM' },
                  { from: 'Coach Davis', msg: 'No problem. Make sure he warms up before checking in. Lets get this W!', time: '5:18 PM', admin: true },
                  { from: 'Mike Williams', msg: 'Can we get the scouting report for Rock Hill? Want to prep DeAndre tonight.', time: '6:45 PM' },
                  { from: 'Coach Davis', msg: 'Just posted it in the Game Hub film sessions. Check it out.', time: '6:48 PM', admin: true },
                ].map((m, i) => (
                  <div key={i} className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-white font-bold text-[9px] shrink-0">
                      {m.from.split(' ').map(w => w[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-semibold">{m.from}</span>
                        {m.admin && <Badge variant="outline" className="text-[8px] px-1 py-0 text-amber-400 border-amber-800">Admin</Badge>}
                        <span className="text-zinc-600 text-[10px]">{m.time}</span>
                      </div>
                      <p className="text-zinc-400 text-xs mt-0.5 leading-relaxed">{m.msg}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <div className="flex-1 bg-zinc-800 rounded-lg px-3 py-2 text-zinc-500 text-xs border border-zinc-700">Type a message...</div>
                <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center cursor-pointer"><Send className="w-3.5 h-3.5 text-white" /></div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Megaphone className="w-3 h-3 text-amber-400" />
                <span className="text-zinc-600 text-[10px]">Coaches can send broadcast announcements to all parents</span>
              </div>
            </CardContent>
            )}

            {teamTab === 'media' && (
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-sm">Team Media</h3>
                <button className="text-[10px] px-3 py-1 rounded-lg bg-cyan-600 text-white font-semibold">+ Upload</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { title: 'Marcus Johnson Highlight', type: 'video', color: '#1f2937' },
                  { title: 'Team Huddle Pre-Game', type: 'photo', color: '#292524' },
                  { title: 'DeAndre Crossover Clip', type: 'video', color: '#1c1917' },
                  { title: 'Bench Celebration', type: 'photo', color: '#374151' },
                  { title: 'Jaylen Poster Dunk', type: 'video', color: '#18181b' },
                  { title: 'Championship Trophy', type: 'photo', color: '#27272a' },
                ].map((m, i) => (
                  <div key={i} className="rounded-lg overflow-hidden border border-zinc-700/50 hover:border-zinc-600 transition-colors">
                    <div className="aspect-video flex items-center justify-center" style={{ backgroundColor: m.color }}>
                      {m.type === 'video' ? <Film className="w-5 h-5 text-zinc-600" /> : <Image className="w-5 h-5 text-zinc-600" />}
                    </div>
                    <div className="p-2 bg-zinc-900">
                      <p className="text-white text-[11px] font-medium truncate">{m.title}</p>
                      <Badge variant="outline" className="text-[8px] px-1 py-0 border-zinc-700 text-zinc-500 mt-1">{m.type}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
            )}
          </Card>
        </section>

        {/* ============ MONETIZATION ============ */}
        <section id="demo-monetization">
          <SectionHeader icon={<DollarSign />} title="Monetization" subtitle="Multiple revenue streams built in" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[
              { 
                name: 'Rooam Basic', 
                tagline: 'Watch. Follow. Stay Connected.',
                price: '$3.00', priceSuffix: '/mo',
                billing: '$8.99 billed quarterly',
                annual: '$35.96/year',
                color: 'border-zinc-600', 
                features: ['Live game streaming', 'Full replay access (no limits)', 'Follow teams, players & events', 'Personalized viewing feed', 'Mobile, tablet & desktop access'],
                excluded: ['No highlight clipping', 'No athlete profile']
              },
              { 
                name: 'Rooam Plus', 
                tagline: 'Create. Clip. Build Your Profile.',
                price: '$10.00', priceSuffix: '/mo',
                billing: '$29.99 billed quarterly',
                annual: '$119.96/year',
                color: 'border-cyan-500', 
                popular: true, 
                features: ['Everything in Basic, plus:', 'Highlight clipping tool', 'Download & share clips', 'Tag players in clips', 'Athlete profile (claim & manage)', 'Highlight storage & organization']
              },
              { 
                name: 'Rooam Elite', 
                tagline: 'Advanced Stats. Exposure. Recruiting.',
                price: '$25.00', priceSuffix: '/mo',
                billing: '$74.99 billed quarterly',
                annual: '$299.96/year',
                color: 'border-red-500', 
                features: ['Everything in Plus, plus:', 'AI-generated highlights (SportsVisio)', 'Full box scores & advanced stats', 'AI game breakdowns & coach reports', 'Claim your stats across events', 'Career stat tracking & game logs', 'Searchable recruiting profile', 'Priority processing for highlights']
              },
            ].map((tier, i) => (
              <Card key={i} className={`bg-zinc-900/50 ${tier.color} border-2 relative overflow-hidden`}>
                {tier.popular && (
                  <div className="absolute top-0 right-0 bg-cyan-500 text-black text-[10px] font-bold px-3 py-0.5 rounded-bl-lg">
                    MOST POPULAR
                  </div>
                )}
                <CardContent className="p-5">
                  <h3 className="text-white font-bold text-lg">{tier.name}</h3>
                  <p className="text-zinc-400 text-xs mb-3">{tier.tagline}</p>
                  <p className="text-2xl font-black text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{tier.billing}</p>
                  <div className="mt-2 space-y-1 text-xs text-zinc-500">
                    <p>{tier.annual}</p>
                  </div>
                  <ul className="mt-4 space-y-2">
                    {tier.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-zinc-400 text-xs">
                        <Star className="w-3 h-3 text-emerald-500 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {tier.excluded && (
                    <ul className="mt-2 space-y-1">
                      {tier.excluded.map((f, j) => (
                        <li key={j} className="flex items-center gap-2 text-zinc-600 text-xs">
                          <span className="w-3 h-3 shrink-0 text-center leading-3">&times;</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Revenue */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Event Passes</h3>
                    <p className="text-zinc-500 text-xs">One-time access for specific tournaments</p>
                  </div>
                </div>
                <p className="text-zinc-400 text-sm">Parents and fans can purchase single-event access without a full membership. Tiered pricing per event (Day Pass, Full Event, VIP).</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Program Partnerships</h3>
                    <p className="text-zinc-500 text-xs">B2B revenue from organizations</p>
                  </div>
                </div>
                <p className="text-zinc-400 text-sm">Youth sports organizations pay for white-label streaming, team management tools, and tournament infrastructure at scale.</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ============ CREATOR DASHBOARD ============ */}
        <section id="demo-creator" data-testid="demo-creator-section">
          <SectionHeader icon={<Wallet />} title="Creator Dashboard" subtitle="Your transparent revenue hub — 70% of every ad dollar goes straight to you" />

          {/* 70/30 split banner */}
          <Card className="bg-gradient-to-r from-red-600/20 via-red-600/10 to-transparent border-red-600/30 mb-6">
            <CardContent className="p-5 sm:p-7">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex-1">
                  <Badge className="bg-red-600/30 text-red-300 border border-red-600/40 mb-3">CREATOR PAYOUTS</Badge>
                  <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    YOU KEEP <span className="text-red-500">70%</span> OF EVERY AD DOLLAR
                  </h3>
                  <p className="text-zinc-400 text-sm mt-2 max-w-xl">
                    Ads are tied to every game via your HLS stream URL. The more people watch, the more revenue is generated — and 70% of that lands in your payout.
                    Rooam keeps 30% to cover streaming infrastructure, bandwidth, and promotion.
                  </p>
                </div>
                {/* Split chart — centered on mobile, right-aligned on desktop */}
                <div className="flex items-center justify-center md:justify-end gap-5 shrink-0 w-full md:w-auto">
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28">
                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#27272a" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#ef4444" strokeWidth="3" strokeDasharray="70 30" strokeLinecap="butt" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-lg sm:text-xl font-black text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>70/30</span>
                      <span className="text-[10px] text-zinc-500 uppercase tracking-wider">split</span>
                    </div>
                  </div>
                  <div className="text-sm space-y-2">
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" /><span className="text-white font-semibold">Creator</span><span className="text-zinc-500 tabular-nums">70%</span></div>
                    <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-zinc-700 shrink-0" /><span className="text-white font-semibold">Rooam</span><span className="text-zinc-500 tabular-nums">30%</span></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Overview — mocked dashboard */}
          <Card className="bg-zinc-900/50 border-zinc-800 mb-6" data-testid="creator-revenue-overview">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-red-500" />
                  <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>REVENUE OVERVIEW</h3>
                </div>
                <div className="flex items-center gap-1 rounded-lg bg-zinc-800/60 p-1 text-xs">
                  {['Today', 'Yesterday', 'Month', 'Last Month'].map((l, i) => (
                    <span key={l} className={`px-2.5 py-1 rounded-md ${i === 2 ? 'bg-red-600 text-white' : 'text-zinc-500'}`}>{l}</span>
                  ))}
                </div>
              </div>

              {/* Summary tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'This Month Revenue', value: '$412', sub: '+42% vs last month', icon: <DollarSign />, accent: 'text-emerald-400' },
                  { label: 'Your 70% Payout', value: '$288', sub: 'Due Apr 1', icon: <Wallet />, accent: 'text-red-400' },
                  { label: 'Impressions', value: '42,360', sub: '7-day', icon: <Eye />, accent: 'text-cyan-400' },
                  { label: 'eCPM', value: '$9.72', sub: 'Rolling avg', icon: <TrendingUp />, accent: 'text-amber-400' },
                ].map((t, i) => (
                  <div key={i} className="rounded-xl bg-black/40 border border-zinc-800 p-4">
                    <div className={`${t.accent} mb-2`}>{t.icon}</div>
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500">{t.label}</p>
                    <p className="text-2xl font-black text-white mt-0.5" style={{ fontFamily: 'Oswald, sans-serif' }}>{t.value}</p>
                    <p className="text-[11px] text-emerald-400 mt-0.5 flex items-center gap-1">
                      <ArrowUpRight className="w-3 h-3" />{t.sub}
                    </p>
                  </div>
                ))}
              </div>

              {/* 7-day Impressions & eCPM chart */}
              <div className="rounded-xl bg-black/40 border border-zinc-800 p-4 mb-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-white">Impressions &amp; eCPM — Last 7 Days</p>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="flex items-center gap-1 text-emerald-400"><span className="w-2 h-2 rounded-full bg-emerald-400" />Impressions</span>
                    <span className="flex items-center gap-1 text-red-400"><span className="w-2 h-2 rounded-full bg-red-400" />eCPM</span>
                  </div>
                </div>
                <svg viewBox="0 0 700 180" className="w-full h-32 sm:h-44" preserveAspectRatio="none" aria-hidden="true">
                  {/* grid */}
                  {[0, 1, 2, 3, 4].map(i => (
                    <line key={i} x1="0" x2="700" y1={20 + i * 35} y2={20 + i * 35} stroke="#27272a" strokeWidth="1" strokeDasharray="2 4" />
                  ))}
                  {/* impressions area (upward trend) */}
                  <defs>
                    <linearGradient id="impArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.45" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,160 L100,150 L200,140 L300,120 L400,95 L500,65 L600,40 L700,18 L700,180 L0,180 Z" fill="url(#impArea)" />
                  <path d="M0,160 L100,150 L200,140 L300,120 L400,95 L500,65 L600,40 L700,18" fill="none" stroke="#10b981" strokeWidth="2.5" />
                  {/* eCPM line (wavy) */}
                  <path d="M0,130 L100,110 L200,122 L300,100 L400,85 L500,72 L600,60 L700,55" fill="none" stroke="#ef4444" strokeWidth="2" strokeDasharray="4 3" />
                  {/* dots on impressions */}
                  {[[0, 160], [100, 150], [200, 140], [300, 120], [400, 95], [500, 65], [600, 40], [700, 18]].map(([x, y], i) => (
                    <circle key={i} cx={x} cy={y} r="3" fill="#10b981" />
                  ))}
                </svg>
                <div className="flex justify-between text-[10px] text-zinc-600 mt-1 px-1">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Today'].map(d => (<span key={d}>{d}</span>))}
                </div>
              </div>

              {/* Events breakdown — responsive */}
              <div className="rounded-xl bg-black/40 border border-zinc-800 overflow-hidden">
                {/* Header — desktop only */}
                <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-800 text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                  <div className="col-span-5">Event</div>
                  <div className="col-span-2 text-right">Views</div>
                  <div className="col-span-2 text-right">Ad Revenue</div>
                  <div className="col-span-2 text-right">Your 70%</div>
                  <div className="col-span-1 text-right">CTR</div>
                </div>
                {[
                  { name: 'Elbert Kinnebrew Cali Classic', date: 'Apr 11-12, 2026', views: '12,840', rev: '$124.80', share: '$87.36', ctr: '0.42%', live: true },
                  { name: 'Spring Showcase — Charlotte', date: 'Mar 22-24, 2026', views: '8,415', rev: '$81.80', share: '$57.26', ctr: '0.38%' },
                  { name: '17U Pool Play — Vegas Invite', date: 'Mar 8-10, 2026', views: '6,202', rev: '$60.28', share: '$42.20', ctr: '0.36%' },
                  { name: 'Sunday Test Games', date: 'Feb 18, 2026', views: '1,973', rev: '$19.20', share: '$13.44', ctr: '0.29%' },
                ].map((e, i) => (
                  <div key={i} className="px-4 py-3 border-b border-zinc-800/60 last:border-b-0 hover:bg-white/5 transition-colors">
                    {/* Mobile: stacked card layout */}
                    <div className="sm:hidden">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-white font-semibold text-sm truncate">{e.name}</span>
                        {e.live && <Badge className="bg-red-600/30 text-red-300 border border-red-600/40 text-[9px] px-1.5 shrink-0">LIVE</Badge>}
                      </div>
                      <p className="text-[10px] text-zinc-500 mb-3">{e.date}</p>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <p className="text-[9px] uppercase tracking-wider text-zinc-500">Views</p>
                          <p className="text-white font-semibold text-sm tabular-nums">{e.views}</p>
                        </div>
                        <div>
                          <p className="text-[9px] uppercase tracking-wider text-zinc-500">Ad Rev</p>
                          <p className="text-white font-semibold text-sm tabular-nums">{e.rev}</p>
                        </div>
                        <div>
                          <p className="text-[9px] uppercase tracking-wider text-zinc-500">Your 70%</p>
                          <p className="text-emerald-400 font-bold text-sm tabular-nums">{e.share}</p>
                        </div>
                        <div>
                          <p className="text-[9px] uppercase tracking-wider text-zinc-500">CTR</p>
                          <p className="text-zinc-300 text-sm tabular-nums">{e.ctr}</p>
                        </div>
                      </div>
                    </div>
                    {/* Desktop: 12-col grid */}
                    <div className="hidden sm:grid grid-cols-12 gap-2 items-center text-sm">
                      <div className="col-span-5">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-semibold truncate">{e.name}</span>
                          {e.live && <Badge className="bg-red-600/30 text-red-300 border border-red-600/40 text-[9px] px-1.5">LIVE</Badge>}
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-0.5">{e.date}</p>
                      </div>
                      <div className="col-span-2 text-right text-zinc-300 tabular-nums">{e.views}</div>
                      <div className="col-span-2 text-right text-white font-semibold tabular-nums">{e.rev}</div>
                      <div className="col-span-2 text-right text-emerald-400 font-bold tabular-nums">{e.share}</div>
                      <div className="col-span-1 text-right text-zinc-500 tabular-nums">{e.ctr}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-zinc-600 mt-3 italic leading-relaxed">
                Ads will be tied to every game via the HLS stream URL at a realistic $7–12 eCPM. Revenue numbers shown are projected; the live ad-network integration is rolling out shortly. Larger events with repeat viewers compound revenue significantly.
              </p>
            </CardContent>
          </Card>

          {/* Stream Schedule + Score editing */}
          <Card className="bg-zinc-900/50 border-zinc-800 mb-6" data-testid="creator-stream-schedule">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-red-500" />
                  <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>STREAM SCHEDULE</h3>
                </div>
                <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">Elbert Kinnebrew Cali Classic · Apr 11</Badge>
              </div>
              <div className="space-y-2">
                {DEMO_SCHEDULE.map((g, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 rounded-lg bg-black/40 border border-zinc-800">
                    <div className="flex items-center gap-2 sm:w-24 shrink-0">
                      {g.status === 'live' && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
                      {g.status === 'final' && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                      {g.status === 'upcoming' && <Clock className="w-4 h-4 text-zinc-500" />}
                      <span className="text-sm font-semibold text-white">{g.time}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{g.home} <span className="text-zinc-600">vs</span> {g.away}</p>
                      <p className="text-[11px] text-zinc-500">{g.division} · {g.court}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {g.score && <span className={`text-sm font-bold tabular-nums ${g.status === 'live' ? 'text-red-400' : 'text-white'}`}>{g.score}</span>}
                      {g.status === 'live' && (
                        <span className="flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-600/15 border border-red-600/30 px-2 py-1 rounded-md">
                          <Pencil className="w-3 h-3" /> Edit Score
                        </span>
                      )}
                      {g.status === 'upcoming' && (
                        <span className="text-xs text-zinc-500 border border-zinc-800 px-2 py-1 rounded-md">Start Stream</span>
                      )}
                      {g.status === 'final' && (
                        <span className="text-xs text-emerald-400 border border-emerald-900/60 px-2 py-1 rounded-md">Final</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-zinc-600 mt-3 italic">Creators update scores inline during live games — changes push to every viewer instantly.</p>
            </CardContent>
          </Card>

          {/* Gary Charles Case Study */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
            <Card className="lg:col-span-3 bg-zinc-900/50 border-zinc-800" data-testid="creator-gary-charles-case">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-amber-400" />
                    <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>CASE STUDY — GARY CHARLES HOOPS CLASSIC</h3>
                  </div>
                  <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px]">Jan 3–4, 2026 · NYC</Badge>
                </div>
                <p className="text-zinc-400 text-sm mb-4">
                  A real two-day tournament ran on Rooam. Here's what a creator of this size can expect:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {[
                    { v: '8,900+', l: 'Total Digital Views' },
                    { v: '648', l: 'Unique Live Viewers' },
                    { v: '4,548', l: 'Stream Impressions' },
                    { v: '4,400', l: 'Instagram Views' },
                    { v: '152', l: 'QR Scans' },
                    { v: '361', l: 'Email Subscribers' },
                    { v: '393', l: 'Profile Actions' },
                    { v: '100%', l: 'Delivery Reliability' },
                  ].map((s, i) => (
                    <div key={i} className="rounded-lg bg-black/40 border border-zinc-800 p-3 text-center">
                      <p className="text-xl font-black text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{s.v}</p>
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{s.l}</p>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg bg-black/40 border border-zinc-800 p-4">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Audience Demographics</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div><span className="text-zinc-500">Gender:</span> <span className="text-white font-semibold">75.9% M / 24.1% F</span></div>
                    <div><span className="text-zinc-500">Age:</span> <span className="text-white font-semibold">25–54 core</span></div>
                    <div><span className="text-zinc-500">Geo:</span> <span className="text-white font-semibold">97.4% US (NYC · LA · Nassau)</span></div>
                  </div>
                </div>

                {/* Revenue generated — actual figures from Rooam post-event report */}
                <div className="mt-4 rounded-lg bg-gradient-to-r from-emerald-600/15 via-emerald-600/5 to-transparent border border-emerald-600/25 p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">Ad Revenue Generated</p>
                      <p className="text-[11px] text-zinc-500">2-day tournament · Jan 3–4, 2026</p>
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px]">70/30 SPLIT</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="rounded-lg bg-black/40 border border-zinc-800 p-3">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500">Total Ad Revenue</p>
                      <p className="text-2xl font-black text-white mt-0.5" style={{ fontFamily: 'Oswald, sans-serif' }}>$4,057</p>
                    </div>
                    <div className="rounded-lg bg-emerald-600/10 border border-emerald-600/30 p-3">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-400">Creator 70%</p>
                      <p className="text-2xl font-black text-emerald-400 mt-0.5" style={{ fontFamily: 'Oswald, sans-serif' }}>$2,839.90</p>
                    </div>
                    <div className="rounded-lg bg-black/40 border border-zinc-800 p-3">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500">Rooam 30%</p>
                      <p className="text-2xl font-black text-zinc-300 mt-0.5" style={{ fontFamily: 'Oswald, sans-serif' }}>$1,217.10</p>
                    </div>
                  </div>
                  <div className="rounded-md bg-black/30 border border-zinc-800/60 p-3 text-[11px] text-zinc-400 leading-relaxed">
                    <p className="text-zinc-500 uppercase tracking-wider text-[10px] font-semibold mb-1">How the split works</p>
                    Gross ad revenue generated across every game of the 2-day event: <span className="text-white font-semibold tabular-nums">$4,057</span>.
                    Creator receives <span className="text-emerald-400 font-semibold">70%</span> (<span className="text-emerald-400 font-semibold tabular-nums">$2,839.90</span>) · Rooam retains <span className="text-white font-semibold">30%</span> (<span className="text-white font-semibold tabular-nums">$1,217.10</span>) to cover streaming, bandwidth, and promotion.
                    Larger events with repeat viewers scale this significantly.
                  </div>
                </div>
                <p className="text-[11px] text-zinc-600 mt-3 italic leading-relaxed">
                  Source: official Rooam post-event report — Gary Charles Hoops Classic, Jan 3–4, 2026.
                </p>
              </CardContent>
            </Card>

            {/* Social Promotion */}
            <Card className="lg:col-span-2 bg-zinc-900/50 border-zinc-800 overflow-hidden" data-testid="creator-social-promo">
              <div className="relative aspect-[4/5] bg-black">
                <img
                  src="https://customer-assets.emergentagent.com/job_ba133167-9388-4c67-b1ad-6c77f0bcb4ef/artifacts/5py8o6as_5472BF6F-FFB9-427B-8309-C624615A6B12.jpeg"
                  alt="Elbert Kinnebrew Cali Classic — sample Rooam social promo graphic"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3">
                  <Badge className="bg-black/80 backdrop-blur text-white border border-white/20 text-[10px]">SAMPLE PROMO</Badge>
                </div>
              </div>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Megaphone className="w-4 h-4 text-red-500" />
                  <h3 className="text-white font-bold">Social Promotion — Built In</h3>
                </div>
                <p className="text-zinc-400 text-sm">
                  Every event gets branded promo graphics from our team, pushed across Rooam's social channels to drive viewers to your stream.
                  More eyes on your stream = more ad impressions = more revenue in your pocket.
                </p>
                <div className="mt-3 flex items-center gap-3 text-[11px] text-zinc-500">
                  <span className="flex items-center gap-1"><Share2 className="w-3 h-3" /> IG · TikTok · X</span>
                  <span className="flex items-center gap-1"><UserPlus className="w-3 h-3" /> Audience amplification</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary row — the flywheel */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-5 sm:p-6">
              <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-red-500" />
                The Creator Flywheel — in a nutshell
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {[
                  { n: '1', title: 'You produce the event', desc: 'Rooam streams it live and records every game automatically.' },
                  { n: '2', title: 'We promote it', desc: 'Branded graphics + social pushes drive viewers to your broadcast.' },
                  { n: '3', title: 'Ads serve on the stream', desc: 'HLS-level ad insertion runs across every game — no extra work from you.' },
                  { n: '4', title: 'You get 70% of the revenue', desc: 'Transparent dashboard. Clear monthly payouts. No hidden math.' },
                ].map(s => (
                  <div key={s.n} className="rounded-xl bg-black/40 border border-zinc-800 p-4">
                    <div className="w-7 h-7 rounded-full bg-red-600/20 text-red-400 border border-red-600/40 flex items-center justify-center font-black text-sm mb-2">{s.n}</div>
                    <p className="text-white font-semibold text-sm">{s.title}</p>
                    <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Market Stats */}
        <section className="py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '$30B+', label: 'Youth Sports Market' },
              { value: '45M+', label: 'Youth Athletes in US' },
              { value: '3.5M+', label: 'Games Per Year' },
              { value: '$850', label: 'Avg Family Spend/Year' },
            ].map((stat, i) => (
              <div key={i} className="text-center p-4 rounded-xl bg-zinc-900/50 border border-zinc-800">
                <p className="text-2xl sm:text-3xl font-black text-red-500" style={{ fontFamily: 'Oswald, sans-serif' }}>{stat.value}</p>
                <p className="text-zinc-500 text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-zinc-800">
          <h2 className="text-3xl font-black text-white mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
            READY TO INVEST IN THE FUTURE OF YOUTH SPORTS?
          </h2>
          <p className="text-zinc-400 mb-6 max-w-lg mx-auto">
            Rooam Sports is building the infrastructure layer for youth sports — and we're just getting started.
          </p>
          <a href="mailto:support@rooamsports.network">
            <Button className="bg-red-600 hover:bg-red-500 text-white font-bold px-8 py-3 text-lg rounded-xl">
              Get In Touch
            </Button>
          </a>
        </section>
      </div>
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 text-red-500 mb-1">{icon}<span className="text-xs uppercase tracking-widest font-bold">{subtitle}</span></div>
      <h2 className="text-2xl sm:text-3xl font-black text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>{title}</h2>
    </div>
  );
}
